import { type UseQueryOptions, useQuery, keepPreviousData } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import qs from 'qs';
import fileDownload from 'js-file-download';

import type { IError } from '@/api/http';
import { useApiClient } from '@/providers/ApiClientProvider';
import API from '@/lib/API';
import { type PaginationQueries } from '@/features/pagination/pagination.types';
import type { FilterDrawerFormValue } from '@/components/shared/filter-drawer/types';
import type { ResponseApi } from '@/api/types';

import type { AccountGroupResponse } from '../types/account-group-types';

export type GetTransactionDownloadCSVQueries = Partial<FilterDrawerFormValue> & {
  limit?: number;
  page?: number;
};

export interface DownloadAccountGroupCsv {
  fileName: string;
  params?: GetTransactionDownloadCSVQueries;
}

export interface GetAccountGroup extends PaginationQueries {
  name?: string;
}

export const useAccountGroup = (
  query: GetAccountGroup,
  options?: Partial<
    UseQueryOptions<AccountGroupResponse, unknown, AccountGroupResponse, unknown[]>
  >,
) => {
  const { httpWithAuth, api } = useApiClient();

  return useQuery({
    queryKey: ['account-group', query],
    queryFn: async () => {
      const res = await api<AccountGroupResponse>(
        httpWithAuth.get(API.accountGroup, { params: { ...query } }),
      );
      return res;
    },
    placeholderData: keepPreviousData,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...options,
  });
};

export const useDownloadCsvTransactionAccountGroup = (
  options?: UseMutationOptions<unknown, AxiosError<IError>, DownloadAccountGroupCsv>,
) => {
  const { httpWithAuth } = useApiClient();

  return useMutation({
    mutationFn: async ({ fileName, params }: DownloadAccountGroupCsv): Promise<void> => {
      const queryString = qs.stringify(params, {
        arrayFormat: 'repeat',
      });

      const response = await httpWithAuth.get(
        `dashboard/v1/transaction/download/csv?${queryString}`,
        {
          responseType: 'blob',
        },
      );

      const blob = new Blob([response.data], { type: 'text/csv' });
      fileDownload(blob, fileName);
    },
    ...options,
  });
};

interface BusinessGroup {
  id: number;
  name: string;
}
export const useBusinessGroup = (
  query: GetAccountGroup,
  options?: Partial<
    UseQueryOptions<ResponseApi<BusinessGroup[]>, unknown, ResponseApi<BusinessGroup[]>, unknown[]>
  >,
) => {
  const { httpWithAuth, api } = useApiClient();

  return useQuery({
    queryKey: ['business-group', query],
    queryFn: async () => {
      const res = await api<ResponseApi<BusinessGroup[]>>(
        httpWithAuth.get(API.accountGroup + '/business', { params: { ...query } }),
      );
      return res;
    },
    placeholderData: keepPreviousData,
    retry: 1,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    ...options,
  });
};
